import { useState } from 'react'
import { Check, Layers, LayoutDashboard, LayoutGrid, Pencil, Sparkles, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import {
  DASHBOARD_PRESETS,
  MAX_DASHBOARD_NAME_LENGTH,
  STARTER_DASHBOARDS,
  uniqueDashboardName,
  validateDashboardName,
  type DashboardPresetId,
  type DashboardsPrefs,
} from '@/lib/dashboardLayouts'
import { DASHBOARD_MODULE_GROUPS, dashboardWidget } from '@/lib/dashboardWidgets'
import { cn } from '@/lib/utils'
import { moduleStyle } from './moduleStyle'

/** Which module each ready-made dashboard and group card borrows its icon and colour from. */
const STYLE_FROM: Record<string, string> = {
  'starter:patients-quality': 'transplant',
  'starter:process': 'processing',
  'starter:inventory': 'inventory',
  'starter:storage': 'storage',
  'starter:products': 'products',
  'starter:equipment': 'equipment',
  'starter:quality': 'capa',
  'starter:system': 'notifications',
  transplant: 'transplant',
  process: 'mes',
  inventory: 'inventory',
  quality: 'equipment',
}

type PresetCard = { id: DashboardPresetId; label: string; description: string; icon: LucideIcon; tile: string }

function cardFor(id: DashboardPresetId, label: string): PresetCard {
  if (id === 'empty') return { id, label: 'Blank', description: 'Start empty and add the widgets you want.', icon: LayoutGrid, tile: 'bg-gray-400' }
  if (id === 'all') return { id, label, description: 'Every widget you can see, across all modules.', icon: Layers, tile: 'bg-slate-700' }
  if (id === 'starter:overview') {
    return { id, label, description: 'The main numbers across the whole system.', icon: Sparkles, tile: 'bg-blue-600' }
  }
  const style = moduleStyle(STYLE_FROM[id] ?? '')
  const starter = STARTER_DASHBOARDS.find(d => d.id === id)
  const group = DASHBOARD_MODULE_GROUPS.find(g => g.id === id)
  const description = starter
    ? starter.widgets
        .slice(0, 3)
        .map(w => dashboardWidget(w.id)?.title)
        .filter(Boolean)
        .join(', ')
    : group
      ? `Every widget for ${group.modules.map(m => m.label).join(', ')}.`
      : ''
  return { id, label, description, icon: style?.icon ?? LayoutDashboard, tile: style?.tile ?? 'bg-gray-500' }
}

/**
 * Names a dashboard: a new one (and what it starts from), or an existing one
 * being renamed. Same validation as Home layouts: required, at most 40
 * characters, and not a name the user already has.
 */
export function DashboardNameDialog({
  open,
  onOpenChange,
  prefs,
  renaming,
  widgetCount,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  prefs: DashboardsPrefs
  /** The dashboard being renamed; omitted for a new one. */
  renaming?: { id: string; name: string }
  /** How many widgets a preset would add for this user, shown on its card. */
  widgetCount?: (preset: DashboardPresetId) => number
  onSave: (name: string, preset: DashboardPresetId) => void
}) {
  const [name, setName] = useState(renaming?.name ?? '')
  const [preset, setPreset] = useState<DashboardPresetId>('empty')
  const [touched, setTouched] = useState(false)
  // True once the user types a name of their own; until then the name follows
  // whichever card is picked.
  const [nameEdited, setNameEdited] = useState(!!renaming)
  const error = validateDashboardName(prefs, name, renaming?.id)

  const close = (next: boolean) => {
    if (!next) {
      setName(renaming?.name ?? '')
      setPreset('empty')
      setTouched(false)
      setNameEdited(!!renaming)
    }
    onOpenChange(next)
  }

  const cards = DASHBOARD_PRESETS.map(p => cardFor(p.id, p.label))
  const sections = [
    { title: 'Blank', cards: cards.filter(c => c.id === 'empty') },
    { title: 'Ready-made dashboards', cards: cards.filter(c => c.id.startsWith('starter:')) },
    { title: 'By module', cards: cards.filter(c => c.id !== 'empty' && !c.id.startsWith('starter:')) },
  ]
  // Pick a preset; the name follows the card until the user types their own.
  // An emptied field counts as not edited, so it starts following again.
  const choose = (card: PresetCard) => {
    setPreset(card.id)
    if (!nameEdited || !name.trim()) {
      setName(card.id === 'empty' ? '' : uniqueDashboardName(prefs, card.label))
      setNameEdited(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className={cn('gap-0 overflow-hidden p-0', renaming ? 'max-w-md' : 'max-w-3xl')}>
        <form
          onSubmit={e => {
            e.preventDefault()
            setTouched(true)
            if (error) return
            onSave(name.trim(), preset)
            close(false)
          }}
          className='flex max-h-[min(88vh,760px)] flex-col'
        >
          {/* Header */}
          <div className='flex items-start gap-3 border-b border-gray-100 px-6 pb-4 pt-6'>
            <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-sm'>
              {renaming ? <Pencil className='h-5 w-5 text-white' /> : <LayoutDashboard className='h-5 w-5 text-white' />}
            </span>
            <div>
              <DialogTitle className='text-base font-semibold text-gray-900'>
                {renaming ? 'Rename dashboard' : 'Create a dashboard'}
              </DialogTitle>
              <DialogDescription className='mt-0.5 text-sm text-gray-500'>
                {renaming
                  ? 'Give this dashboard a name you will recognise.'
                  : 'Name it, choose what it starts with, and change the widgets any time after.'}
              </DialogDescription>
            </div>
          </div>

          <div className='flex-1 overflow-y-auto px-6 py-5'>
            {/* Name */}
            <label className='block'>
              <span className='mb-1.5 flex items-baseline justify-between'>
                <span className='text-sm font-medium text-gray-800'>Name</span>
                <span className={cn('text-[11px] tabular-nums', name.length > MAX_DASHBOARD_NAME_LENGTH - 5 ? 'text-amber-600' : 'text-gray-400')}>
                  {name.length}/{MAX_DASHBOARD_NAME_LENGTH}
                </span>
              </span>
              <input
                autoFocus
                value={name}
                maxLength={MAX_DASHBOARD_NAME_LENGTH}
                onChange={e => {
                  setName(e.target.value)
                  setNameEdited(e.target.value.trim() !== '')
                }}
                placeholder='For example “Inventory” or “QA review”'
                aria-label='Dashboard name'
                aria-invalid={touched && !!error}
                className={cn(
                  'h-11 w-full rounded-xl border bg-white px-4 text-sm outline-none transition-shadow placeholder:text-gray-400',
                  touched && error
                    ? 'border-red-300 focus:ring-4 focus:ring-red-100'
                    : 'border-gray-200 focus:border-blue-400 focus:ring-4 focus:ring-blue-100',
                )}
              />
              {touched && error && <span className='mt-1.5 block text-xs text-red-600'>{error}</span>}
            </label>

            {/* Start with */}
            {!renaming &&
              sections.map(section => (
                <section key={section.title} className='mt-6'>
                  <h3 className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>{section.title}</h3>
                  <div role='radiogroup' aria-label={section.title} className='grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3'>
                    {section.cards.map(card => {
                      const selected = preset === card.id
                      const count = widgetCount?.(card.id)
                      const Icon = card.icon
                      return (
                        <button
                          key={card.id}
                          type='button'
                          role='radio'
                          aria-checked={selected}
                          onClick={() => choose(card)}
                          className={cn(
                            'group relative flex items-start gap-3 rounded-xl border bg-white p-3 text-left transition-all',
                            selected
                              ? 'border-blue-500 ring-4 ring-blue-100'
                              : 'border-gray-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md',
                          )}
                        >
                          <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', card.tile)}>
                            <Icon className='h-4 w-4 text-white' />
                          </span>
                          <span className='min-w-0 flex-1'>
                            <span className='flex items-center gap-1.5'>
                              <span className='truncate text-sm font-semibold text-gray-900'>{card.label}</span>
                            </span>
                            <span className='mt-0.5 line-clamp-2 block text-xs leading-relaxed text-gray-500'>{card.description}</span>
                            {count !== undefined && card.id !== 'empty' && (
                              <span className='mt-1.5 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600'>
                                {count} widget{count === 1 ? '' : 's'}
                              </span>
                            )}
                          </span>
                          <span
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                              selected ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white',
                            )}
                            aria-hidden
                          >
                            {selected && <Check className='h-3 w-3 text-white' />}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </section>
              ))}
          </div>

          {/* Footer */}
          <div className='flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5'>
            <p className='truncate text-xs text-gray-500'>
              {renaming
                ? ' '
                : preset === 'empty'
                  ? 'Starts blank.'
                  : `Starts with ${cards.find(c => c.id === preset)?.label}${
                      widgetCount ? ` · ${widgetCount(preset)} widgets` : ''
                    }.`}
            </p>
            <div className='flex shrink-0 gap-2'>
              <Button type='button' variant='ghost' size='sm' className='rounded-full' onClick={() => close(false)}>
                Cancel
              </Button>
              <Button type='submit' size='sm' className='rounded-full px-5'>
                {renaming ? 'Rename' : 'Create dashboard'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
